<template>
  <nav class="navbar navbar-light bg-light">
    <div class="container-fluid">
      <!-- Logo and brand -->
      <a @click="$emit('showHome')" class="navbar-brand" href="#">
        <div style="overflow: hidden; display: inline-block;">
          <img
            src="../assets/Transparent-Background.png"
            height="100"
            style="margin: 0 -8px;"
            class="d-inline-block align-text-top"
          />
        </div>
      </a>


      <!-- Toggler: visible below 1280px (custom classes) -->
      <button
        class="navbar-toggler toggler-below-1280"
        type="button"
        @click="toggleMenu"
        aria-label="Toggle navigation"
      >
        <span class="navbar-toggler-icon"></span>
      </button>

      <!-- Inline buttons: visible at 1280px and above -->
      <div class="inline-btns-1280 align-items-center">
        
        <!-- Home button when on Utah Draw Info page, otherwise Utah Draw Info button -->
        <button
          v-if="isUtahDrawInfoView"
          class="btn btn-outline-primary me-2"
          @click="$router.push('/')"
        >
          Show Draw Odds
        </button>
        <router-link
          v-else
          to="/utah-draw-info"
          class="btn btn-outline-primary me-2"
        >
          Utah Draw Information
        </router-link>
        
        <!-- "Contact Us" button always visible -->
        <button
          class="btn btn-outline-primary me-2"
          @click="$emit('showFeedback')"
        >
          Contact Us
        </button>
        <!-- Utah Hunt Login button -->
        <router-link
          to="/utah-hunt-login"
          class="btn btn-outline-primary me-2"
        >
          Utah Hunt Login
        </router-link>
        <button
          v-if="isLoggedIn && !isMapView"
          class="btn btn-outline-primary me-2 hunt-finder-btn"
          @click="$router.push('/search-map')"
        >
        Hunt Finder (Map)
        </button>
        <!-- Home button when in map view -->
        <button
          v-if="isLoggedIn && isMapView"
          class="btn btn-outline-primary me-2"
          @click="$router.push('/')"
        >
          Home
        </button>
        <button
          v-if="!isLoggedIn"
          class="btn btn-outline-primary me-2"
          @click="$emit('showSignin')"
        >
          Sign In
        </button>
        <button
          v-if="!isLoggedIn"
          class="btn btn-outline-primary me-2"
          @click="$emit('showSignup')"
        >
          Sign Up
        </button>
        <button
          v-if="isLoggedIn"
          class="btn btn-outline-primary me-2"
          @click="callLogout"
        >
          Sign Out
        </button>
      </div>
    </div>

    <!-- Slideout menu for small screens (below 1280px) -->
    <div :class="['slideout-menu', { 'menu-open': isMenuOpen }]">
      <button class="close-btn" @click="toggleMenu">&times;</button>
      <ul>
        <!-- Home button when on Utah Draw Info page, otherwise Utah Draw Info button in mobile menu -->
        <li>
          <button
            v-if="isUtahDrawInfoView"
            class="btn btn-outline-primary w-100 mb-2"
            @click="handleMenuClick('navigateToHome')"
          >
            See Draw Odds
          </button>
          <router-link
            v-else
            to="/utah-draw-info"
            class="btn btn-outline-primary w-100 mb-2"
            @click="toggleMenu"
          >
          Utah Draw Information
          </router-link>
        </li>
        <li v-if="!isLoggedIn">
          <button class="btn btn-outline-primary w-100 mb-2" @click="handleMenuClick('showSignin')">Sign In</button>
        </li>
        <li v-if="!isLoggedIn">
          <button class="btn btn-outline-primary w-100 mb-2" @click="handleMenuClick('showSignup')">Sign Up</button>
        </li>
        <li v-if="isLoggedIn && !isMapView">
          <button
            class="btn btn-outline-primary w-100 mb-2 hunt-finder-btn"
            @click="handleMenuClick('navigateToMap')"
          >
            Hunt Finder (Map)
          </button>
        </li>
        <!-- Home option in the slideout menu (when in map view) -->
        <li v-if="isMapView">
          <button
            class="btn btn-outline-primary w-100 mb-2"
            @click="handleMenuClick('navigateToHome')"
          >
            Show Draw Odds
          </button>
        </li>
        <!-- "Contact Us" option in the slideout menu -->
        <li>
          <button
            class="btn btn-outline-primary w-100 mb-2"
            @click="handleMenuClick('showFeedback')"
          >
            Contact Us
          </button>
        </li>
        <!-- Utah Hunt Login in the slideout menu -->
        <li>
          <router-link
            to="/utah-hunt-login"
            class="btn btn-outline-primary w-100 mb-2"
            @click="toggleMenu"
          >
            Utah Hunt Login
          </router-link>
        </li>

        <li v-if="isLoggedIn">
          <button class="btn btn-outline-primary w-100 mb-2" @click="handleMenuClick('callLogout')">Sign Out</button>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";

export default {
  name: "Navbar",
  beforeDestroy() {
    // Clean up event listener when component is destroyed
    document.removeEventListener('click', this.handleClickOutside);
  },
  data() {
    return {
      isMenuOpen: false,
    };
  },
  computed: {
    ...mapState(["user"]),
    ...mapGetters(["isAuthenticated"]),
    isLoggedIn() {
      return this.isAuthenticated;
    },
    isMapView() {
      return this.$route.path === '/search-map';
    },
    isUtahDrawInfoView() {
      return this.$route.path === '/utah-draw-info';
    }
  },
  methods: {
    ...mapActions(["logout"]),

    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      
      if (this.isMenuOpen) {
        // Add click outside listener when menu opens
        setTimeout(() => {
          document.addEventListener('click', this.handleClickOutside);
        }, 0);
        // Add class to body when menu is open
        document.body.classList.add('navbar-menu-open');
      } else {
        // Remove listener when menu closes
        document.removeEventListener('click', this.handleClickOutside);
        // Remove class from body when menu is closed
        document.body.classList.remove('navbar-menu-open');
      }
    },

    handleClickOutside(event) {
      const menu = document.querySelector('.slideout-menu');
      const toggler = document.querySelector('.navbar-toggler');
      
      // Check if click is outside both menu and toggler
      if (menu && !menu.contains(event.target) && !toggler.contains(event.target)) {
        this.isMenuOpen = false;
        document.removeEventListener('click', this.handleClickOutside);
      }
    },

    async callLogout() {
      try {
        const response = await fetch(`${this.$API_BASE_URL}/api/customers/logout`, {
          method: "POST",
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        this.logout(); // triggers Vuex logout
        this.$emit('showHome');
      } catch (error) {
        console.error("Logout failed:", error);
        this.logout();
      }
    },

    handleMenuClick(action) {
      // Close the slideout menu
      this.toggleMenu();

      // Emit the appropriate action or call the logout method
      if (action === "callLogout") {
        this.callLogout();
      } else if (action === "navigateToMap") {
        this.$router.push('/search-map');
      } else if (action === "navigateToHome") {
        this.$router.push('/');
      } else {
        this.$emit(action);
      }
    },
  },
};
</script>

<style scoped>
.navbar-toggler {
  border: none;
  background-color: transparent;
}

/* Base styling for the slideout menu */
.slideout-menu {
  position: fixed;
  top: 0;
  right: -250px; /* Start off-screen */
  height: 100%;
  width: 250px;
  background-color: #f8f9fa;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
  padding: 20px;
  z-index: 1050;
  transition: right 0.3s ease-in-out;
}

.slideout-menu.menu-open {
  right: 0;
}

.slideout-menu ul {
  list-style: none;
  padding: 0;
}

.slideout-menu li {
  margin: 15px 0;
}

.slideout-menu .close-btn {
  background: none;
  border: none;
  font-size: 30px;
  float: right;
}

/*-------------------------------------------
  MEDIA QUERIES for 1280px cutoff
-------------------------------------------*/

/* Below 1280px is "mobile" -> Toggler visible, inline hidden */
@media (max-width: 1279px) {
  .inline-btns-1280 {
    display: none !important;
  }
  .toggler-below-1280 {
    display: block !important;
  }
}

/* At 1280px and above -> Toggler hidden, inline visible, slideout hidden */
@media (min-width: 1280px) {
  .toggler-below-1280 {
    display: none !important;
  }

  .inline-btns-1280 {
    display: flex !important;
    align-items: center;
  }

  .slideout-menu {
    display: none !important;
  }
}

/* Golden shimmer effect with trailing gold */
.hunt-finder-btn {
  position: relative;
  overflow: hidden;
  border-color: #FFD700 !important; /* Gold outline */
}

/* Single enhanced shimmer effect */
.hunt-finder-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 215, 0, 0) 0%,
    rgba(255, 215, 0, 0.3) 20%,
    rgba(255, 223, 0, 0.8) 50%,
    rgba(255, 215, 0, 0.3) 80%,
    rgba(255, 215, 0, 0) 100%
  );
  transform: skewX(-20deg);
  z-index: 1; /* Above button background */
  animation: shimmer 3s infinite linear;
  pointer-events: none; /* Allow clicks to pass through */
}

/* Animation for shimmer effect */
@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 200%;
  }
}

/* Hover effect - golden background with blue text */
.hunt-finder-btn:hover {
  background-color: rgba(255, 215, 0, 0.2);
  color: #0d6efd !important; /* Keep text blue */
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
}
</style>
